import Icon from '@atoms/Icon/Icon';
import { IAccordionItem } from '@models/IAccordionItem';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { PropsWithChildren } from 'react';

interface Props extends IAccordionItem {
  readonly index: number;
}

const AccordionItem = ({
  activeIndex,
  background = false,
  index,
  isActive = false,
  meta,
  setActiveIndex,
  text,
  title,
  dataTestId = 'CT-28-AccordionItem',
  children,
  icon,
  onClick,
}: PropsWithChildren<Props>) => (
  <div data-epi-block-id={meta?.contentLink} data-testid={dataTestId}>
    <button
      type="button"
      onClick={() => {
        const isClosing = activeIndex === index;

        updateDataLayer({
          event: children ? 'accordion' : 'faq',
          content_type: isClosing ? 'closed' : 'opened',
          link_text: title,
        });

        setActiveIndex?.(isClosing ? -1 : index);
        onClick?.();
      }}
      aria-expanded={isActive ? 'true' : 'false'}
      aria-controls={`accordion-panel-${index}`}
      className={classNames(
        isActive ? 'border-b' : 'border-0',
        { 'dark:border-grey-600': !background },
        'flex w-full items-center border-grey border-t p-5 text-left'
      )}
    >
      {icon && <Icon className="mr-2.5" name={icon} size="1.25rem" />}
      <p
        className="h6"
        data-epi-property-name={meta?.editHints?.title}
        data-testid={`${dataTestId}-title`}
      >
        {title}
      </p>

      <motion.div
        className="ml-auto flex origin-center"
        animate={{
          rotate: isActive ? 180 : 0,
        }}
      >
        <Icon
          name="caret_down"
          colorClassName={classNames(
            background ? 'dark:text-white' : 'dark:text-yellow',
            'text-black-100'
          )}
        />
      </motion.div>
    </button>

    <AnimatePresence>
      {isActive && (
        <motion.div
          style={{ overflow: 'hidden' }}
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
        >
          <div
            id={`accordion-panel-${index}`}
            className="body-two px-5 py-5 content-area lg:py-10"
          >
            {children ||
              (text && (
                <span
                  dangerouslySetInnerHTML={{ __html: text }}
                  className={classNames(
                    background ? 'dark:text-grey-400' : 'dark:text-white',
                    'body-two text-grey-450 content-area'
                  )}
                  data-epi-property-name={meta?.editHints?.text}
                  data-testid={`${dataTestId}-text`}
                />
              ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default AccordionItem;
